import { useCallback, useEffect, useState } from 'react'
import { agentesMemoriaApi } from '../api/agentesMemoria'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import { colors, radius, space, sx, text } from '../design/tokens'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'

// SISTEMA → Memoria de agentes IA (Martin, 1 oct 2026 — Cerebro Comercial,
// etapa 1). Una sola pantalla para las reglas de todos los agentes: las que
// proponen solos esperan acá; quien aprueba elige el alcance (comercial =
// todos los canales). Arriba, si cada agente las está USANDO: la versión de
// memoria de su última respuesta y cuántas veces citó cada regla — "aprende"
// se ve acá, no se cuenta.

const ESTADOS = {
  propuesta: { label: 'Propuesta', background: colors.warnBg, color: colors.warn },
  vigente: { label: 'Vigente', background: colors.okBg, color: colors.ok },
  archivada: { label: 'Archivada', background: colors.bgSoft, color: colors.textSub },
}
const TIPOS = { regla: 'Regla', leccion: 'Lección', tono: 'Tono', proceso: 'Proceso' }
const FILTROS = [
  { id: 'propuesta', label: 'Propuestas' },
  { id: 'vigente', label: 'Vigentes' },
  { id: 'archivada', label: 'Archivadas' },
]

export default function AgentesMemoriaPage() {
  const [datos, setDatos] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [filtro, setFiltro] = useState('propuesta')
  const [agente, setAgente] = useState('')
  const [nueva, setNueva] = useState(false)

  const cargar = useCallback(async () => {
    setCargando(true); setError(null)
    try {
      const r = await agentesMemoriaApi.listar()
      setDatos(r.data)
    } catch {
      setError('No se pudo cargar la memoria.')
    } finally { setCargando(false) }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const agentes = datos?.agentes ?? []
  const memorias = datos?.memorias ?? []
  const nombreDe = (slug) => agentes.find(a => a.slug === slug)?.nombre ?? slug
  const conteo = (estado) => memorias.filter(m => m.estado === estado).length
  const visibles = memorias.filter(m => m.estado === filtro && (!agente || m.agente === agente))

  return (
    <div style={st.page}>
      <header style={st.header}>
        <div style={st.actions}><h1 style={st.title}>Memoria de agentes IA</h1>{datos && <span style={st.count}>{memorias.length}</span>}</div>
        <div style={st.actions}>
          <Boton type="button" variante="ghost" disabled={cargando} onClick={cargar}>{cargando ? 'Actualizando…' : '↻ Actualizar'}</Boton>
          <Boton type="button" variante="primary" disabled={!datos} onClick={() => setNueva(true)}>+ Nueva regla</Boton>
        </div>
      </header>
      <p style={st.note}>Las reglas que guían al Sommelier y al agente de WhatsApp. Lo que proponen solos espera acá hasta que alguien lo aprueba y elige a quién aplica. Precio, stock y cantidades siguen siendo del sistema: la memoria guarda criterio, no datos.</p>

      {error && <div role="alert" style={st.errorBox}><strong>{error}</strong><Boton type="button" onClick={cargar}>Reintentar</Boton></div>}
      {datos && !datos.memoria_comercial_activa && (
        <div role="status" style={st.aviso}>La memoria comercial está <strong>apagada</strong>: el agente de WhatsApp lee solo sus reglas y el Sommelier no lee ninguna. Se prende en el servidor (MEMORIA_COMERCIAL_ACTIVA) y en la tienda (SOMMELIER_MEMORIA).</div>
      )}

      {datos && (
        <section aria-label="Uso por agente" style={st.agentes}>
          {agentes.map(a => <EstadoAgente key={a.slug} agente={a} nombreDe={nombreDe} />)}
        </section>
      )}

      {cargando && !datos ? <div style={st.empty} role="status">Cargando memoria…</div> : datos && (
        <section aria-label="Reglas">
          <div style={st.listHeader}>
            <div style={st.chips} role="tablist" aria-label="Estado">
              {FILTROS.map(f => (
                <button key={f.id} type="button" role="tab" aria-selected={filtro === f.id} onClick={() => setFiltro(f.id)}
                  style={{ ...sx.pill, ...(filtro === f.id ? sx.pillActive : null) }}>
                  {f.label} <span style={st.chipCount}>{conteo(f.id)}</span>
                </button>
              ))}
            </div>
            <label style={st.filtroAgente}>
              Agente
              <select style={{ ...st.input, width: 200 }} value={agente} onChange={e => setAgente(e.target.value)}>
                <option value="">Todos</option>
                {agentes.map(a => <option key={a.slug} value={a.slug}>{a.nombre}</option>)}
              </select>
            </label>
          </div>
          {visibles.length === 0
            ? <div style={st.empty}>{filtro === 'propuesta' ? 'No hay propuestas esperando aprobación.' : 'No hay reglas en este estado.'}</div>
            : <div style={st.list}>{visibles.map(m => <Regla key={m.id} m={m} agentes={agentes} nombreDe={nombreDe} onCambio={cargar} />)}</div>}
        </section>
      )}

      {nueva && <NuevaRegla agentes={agentes} onClose={() => setNueva(false)} onCreada={() => { setNueva(false); setFiltro('vigente'); cargar() }} />}
    </div>
  )
}

/** Si el agente usa la memoria: compara la versión de su última respuesta con la vigente. */
function EstadoAgente({ agente: a, nombreDe }) {
  const ultima = a.ultima_respuesta
  let uso
  if (a.slug === 'comercial') uso = { texto: 'La leen los demás agentes', tono: 'neutro' }
  else if (!ultima) uso = { texto: 'Todavía no respondió nada', tono: 'neutro' }
  else if (!ultima.memoria_version) uso = { texto: 'Última respuesta sin memoria', tono: 'warn' }
  else if (ultima.memoria_version === a.version_actual) uso = { texto: 'Usa las reglas vigentes', tono: 'ok' }
  else uso = { texto: 'Respondió con una versión anterior', tono: 'warn' }
  const tonos = { ok: { background: colors.okBg, color: colors.ok }, warn: { background: colors.warnBg, color: colors.warn }, neutro: { background: colors.bgSoft, color: colors.textSub } }

  return (
    <article style={st.agente} aria-label={a.nombre}>
      <div style={st.agenteNombre}>{a.nombre}</div>
      <div style={st.meta}>Lee: {a.lee.map(nombreDe).join(' + ')}</div>
      <div style={st.agenteDatos}>
        <span><strong style={st.numero}>{a.reglas_vigentes}</strong> {a.reglas_vigentes === 1 ? 'regla vigente' : 'reglas vigentes'}</span>
        {a.version_actual && <span style={st.version} title="Huella del conjunto de reglas vigentes">v {a.version_actual}</span>}
      </div>
      <span style={{ ...st.badge, ...tonos[uso.tono] }}>{uso.texto}</span>
      {ultima && <div style={st.meta}>Última respuesta: <span style={sx.mono}>{fmtFechaHora(ultima.at)}</span></div>}
      {a.slug === 'comercial' && <div style={st.meta}>No responde a clientes: es la base común.</div>}
    </article>
  )
}

function Regla({ m, agentes, nombreDe, onCambio }) {
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState(null)
  const [alcance, setAlcance] = useState('')
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState(m.contenido)
  const [versiones, setVersiones] = useState(null)
  const estado = ESTADOS[m.estado] ?? ESTADOS.archivada
  const ev = m.evidencia ?? {}
  const uids = ev.conversaciones ?? []
  const ejemplos = ev.ejemplos ?? []

  async function accion(fn, confirmacion) {
    if (confirmacion && !window.confirm(confirmacion)) return
    setOcupado(true); setError(null)
    try {
      await fn()
      setEditando(false)
      onCambio()
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo aplicar la acción.')
    } finally { setOcupado(false) }
  }

  async function verVersiones(e) {
    if (!e.currentTarget.open || versiones) return
    try { setVersiones((await agentesMemoriaApi.versiones(m.id)).data) } catch { setVersiones([]) }
  }

  return (
    <article style={st.regla} aria-label={m.ref}>
      <div style={st.cabecera}>
        <span style={{ ...st.badge, ...estado }}>{estado.label}</span>
        <span style={st.meta}>{nombreDe(m.agente)} · {TIPOS[m.tipo] ?? m.tipo}</span>
        <span style={st.ref}>{m.ref}</span>
        {m.veces_detectada > 1 && <span style={st.meta}>Detectada {m.veces_detectada} veces</span>}
        {m.estado === 'vigente' && <span style={st.meta}>Usada {m.usos_30d} {m.usos_30d === 1 ? 'vez' : 'veces'} en 30 días</span>}
        {m.aprobada_at && m.estado === 'vigente' && <span style={st.meta}>Aprobada el <span style={sx.mono}>{fmtFecha(m.aprobada_at)}</span></span>}
      </div>

      {editando
        ? <textarea data-nocaps style={st.textarea} value={texto} onChange={e => setTexto(e.target.value)} rows={4} maxLength={4000} aria-label="Texto de la regla" />
        : <p style={st.texto}>{m.contenido}</p>}
      {m.motivo && <p style={st.note}>{m.motivo}</p>}

      {(uids.length > 0 || ejemplos.length > 0) && (
        <details style={st.detalle}>
          <summary style={st.summary}>Evidencia: {uids.length} {uids.length === 1 ? 'conversación' : 'conversaciones'}{ejemplos.length > 0 && ` · ${ejemplos.length} ${ejemplos.length === 1 ? 'ejemplo' : 'ejemplos'}`}</summary>
          {uids.length > 0 && <p style={st.uids}>{uids.join(' · ')}</p>}
          {ejemplos.map((e, i) => (
            <div key={i} style={st.ejemplo}>
              <div><span style={st.rol}>Cliente</span>{e.consulta}</div>
              {e.respuesta_texto && <div><span style={st.rol}>Agente</span>{e.respuesta_texto}</div>}
            </div>
          ))}
        </details>
      )}
      <details style={st.detalle} onToggle={verVersiones}>
        <summary style={st.summary}>Historial (v{m.version})</summary>
        {versiones === null ? <p style={st.note}>Cargando…</p> : versiones.map(v => (
          <div key={v.version} style={st.version_fila}><span style={st.ref}>v{v.version}</span> {v.operacion} · {v.estado} · {v.por} · <span style={sx.mono}>{fmtFechaHora(v.created_at)}</span></div>
        ))}
      </details>

      {error && <div role="alert" style={st.error}>{error}</div>}
      <div style={st.actions}>
        {editando ? (
          <>
            <Boton type="button" sm variante="primary" disabled={ocupado || !texto.trim()} onClick={() => accion(() => agentesMemoriaApi.editar(m.id, { contenido: texto }))}>Guardar</Boton>
            <Boton type="button" sm variante="ghost" disabled={ocupado} onClick={() => { setEditando(false); setTexto(m.contenido) }}>Cancelar</Boton>
          </>
        ) : (
          <>
            {m.estado === 'propuesta' && (
              <>
                <select style={{ ...st.input, width: 240 }} value={alcance} onChange={e => setAlcance(e.target.value)} aria-label="A quién aplica">
                  <option value="">¿A quién aplica?</option>
                  {agentes.map(a => <option key={a.slug} value={a.slug}>{a.slug === 'comercial' ? 'Todos los canales (comercial)' : a.nombre}</option>)}
                </select>
                <Boton type="button" sm variante="success" disabled={ocupado || !alcance} onClick={() => accion(() => agentesMemoriaApi.aprobar(m.id, alcance))}>✓ Aprobar</Boton>
              </>
            )}
            {m.estado !== 'archivada' && <Boton type="button" sm variante="secondary" disabled={ocupado} onClick={() => setEditando(true)}>Editar</Boton>}
            {m.estado !== 'archivada' && <Boton type="button" sm variante="destructive" disabled={ocupado} onClick={() => accion(() => agentesMemoriaApi.archivar(m.id), m.estado === 'propuesta' ? 'Una propuesta archivada no se vuelve a proponer. ¿Archivarla?' : 'Los agentes dejan de leer esta regla. ¿Archivarla?')}>Archivar</Boton>}
            {m.estado === 'archivada' && <Boton type="button" sm variante="secondary" disabled={ocupado} onClick={() => accion(() => agentesMemoriaApi.reactivar(m.id))}>Reactivar</Boton>}
          </>
        )}
      </div>
    </article>
  )
}

function NuevaRegla({ agentes, onClose, onCreada }) {
  const [form, setForm] = useState({ agente: '', clave: '', tipo: 'regla', contenido: '' })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))
  const valida = form.agente && /^[a-z0-9][a-z0-9_\-/]{1,118}$/.test(form.clave) && form.contenido.trim()

  async function guardar() {
    setGuardando(true); setError(null)
    try {
      await agentesMemoriaApi.crear(form)
      onCreada()
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo guardar la regla.')
    } finally { setGuardando(false) }
  }

  return (
    <Modal title="Nueva regla" onClose={onClose} maxWidth="560px">
      <div style={st.form}>
        <p style={st.note}>Una regla escrita acá queda vigente al guardarla: la leen los agentes elegidos en su próxima respuesta.</p>
        <label style={st.campo}>A quién aplica
          <select style={st.input} value={form.agente} onChange={set('agente')}>
            <option value="">Elegir…</option>
            {agentes.map(a => <option key={a.slug} value={a.slug}>{a.slug === 'comercial' ? 'Todos los canales (comercial)' : a.nombre}</option>)}
          </select>
        </label>
        <div style={st.fila}>
          <label style={{ ...st.campo, flex: '2 1 220px' }}>Clave
            <input data-nocaps style={st.input} value={form.clave} onChange={set('clave')} placeholder="consejo/opciones" />
            <span style={st.ayuda}>Minúsculas, tipo ruta: tema/detalle.</span>
          </label>
          <label style={{ ...st.campo, flex: '1 1 120px' }}>Tipo
            <select style={st.input} value={form.tipo} onChange={set('tipo')}>
              {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>
        <label style={st.campo}>Regla
          <textarea data-nocaps style={st.textarea} value={form.contenido} onChange={set('contenido')} rows={5} maxLength={4000} placeholder="Cuando el cliente pide un tipo de vino sin decir cuántos, mostrale 4 opciones." />
        </label>
        {error && <div role="alert" style={st.error}>{error}</div>}
        <div style={st.pie}>
          <Boton type="button" variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton type="button" variante="primary" disabled={!valida || guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
        </div>
      </div>
    </Modal>
  )
}

const st = {
  page: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: space.lg, minWidth: 0, color: colors.text, fontSize: text.body },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.lg },
  title: { margin: 0, fontSize: 22, fontWeight: 650, letterSpacing: '-0.025em' },
  actions: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  count: { ...sx.badge, background: colors.bgSoft, color: colors.textSub, border: `1px solid ${colors.border}` },
  note: { margin: 0, color: colors.textSec, fontSize: text.sec, lineHeight: 1.6 },
  meta: { color: colors.textSec, fontSize: text.sec, lineHeight: 1.5 },
  errorBox: { background: colors.errBg, color: colors.err, padding: space.xl, borderRadius: radius.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.lg },
  aviso: { background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, padding: `${space.lg}px ${space.xl}px`, borderRadius: radius.md, fontSize: text.sec, lineHeight: 1.6 },
  agentes: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: space.lg },
  agente: { ...sx.card, display: 'grid', gap: space.sm, alignContent: 'start', justifyItems: 'start', minWidth: 0 },
  agenteNombre: { fontSize: text.title, fontWeight: 600 },
  agenteDatos: { display: 'flex', alignItems: 'baseline', gap: space.lg, flexWrap: 'wrap' },
  numero: { fontSize: text.title, fontWeight: 650, fontVariantNumeric: 'tabular-nums' },
  version: { ...sx.mono, fontSize: text.meta, color: colors.textSec },
  badge: { ...sx.badge, lineHeight: 1.5 },
  listHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: space.lg, flexWrap: 'wrap', marginBottom: space.lg },
  chips: { display: 'flex', gap: space.sm, flexWrap: 'wrap' },
  chipCount: { marginLeft: space.xs, opacity: 0.75, fontVariantNumeric: 'tabular-nums' },
  filtroAgente: { display: 'flex', alignItems: 'center', gap: space.md, fontSize: text.sec, fontWeight: 600, color: colors.textSub },
  input: { ...sx.input, height: 30, color: colors.text, background: colors.bg, boxSizing: 'border-box' },
  list: { display: 'grid', gap: space.md },
  empty: { ...sx.card, textAlign: 'center', padding: '40px 20px', color: colors.textSec, lineHeight: 1.6 },
  regla: { ...sx.card, display: 'grid', gap: space.md, minWidth: 0 },
  cabecera: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px' },
  ref: { ...sx.mono, fontSize: text.meta, color: colors.textSec },
  texto: { margin: 0, fontSize: text.body, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  textarea: { ...sx.input, width: '100%', boxSizing: 'border-box', minHeight: 96, padding: space.md, fontFamily: 'inherit', fontSize: text.body, lineHeight: 1.6, color: colors.text, resize: 'vertical' },
  detalle: { color: colors.textSec, fontSize: text.sec, lineHeight: 1.6 },
  summary: { cursor: 'pointer' },
  uids: { ...sx.mono, margin: `${space.md}px 0 0`, fontSize: text.meta, color: colors.textSub, overflowWrap: 'anywhere' },
  ejemplo: { display: 'grid', gap: space.xs, marginTop: space.md, paddingTop: space.md, borderTop: `1px dashed ${colors.border}`, color: colors.text, overflowWrap: 'anywhere' },
  rol: { ...sx.cardTitle, display: 'inline-block', minWidth: 72 },
  version_fila: { marginTop: space.xs },
  error: { color: colors.err, fontSize: text.sec },
  form: { display: 'grid', gap: space.lg },
  fila: { display: 'flex', gap: space.lg, flexWrap: 'wrap' },
  campo: { display: 'flex', flexDirection: 'column', gap: space.sm, fontSize: text.sec, fontWeight: 600, color: colors.textSub, minWidth: 0 },
  ayuda: { fontWeight: 400, color: colors.textMeta, fontSize: text.meta },
  pie: { display: 'flex', justifyContent: 'flex-end', gap: space.md },
}
